<template>
    <form v-if="status.detail" @submit.prevent="submitForm">
        <b-form-group :label="$t('label.name')" label-cols="3" label-align="left">
            <b-form-input v-model.trim="form.name" :state="fieldState('name')" />
            <b-form-invalid-feedback>{{ $t('message.requiredX', [$t('label.name')]) }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group :label="$t('label.catalogSubcategory')" label-cols="3" label-align="left">
            <multiselect v-model="form.catalogSubcategory" track-by="value" label="text"
                :placeholder="$t('label.searchSelectSubcategory')" :options="catalogSubcategoryDdl"
                :loading="subcategoryLoading" :allow-empty="false" @search-change="searchSubcategories" />
            <div v-if="submitted && !$v.form.catalogSubcategory.required" class="invalid-feedback d-block text-right">
                {{ $t('label.subcategoryRequired') }}
            </div>
        </b-form-group>
        <b-form-group :label="$t('label.description')" label-cols="3" label-align="left">
            <b-form-textarea v-model.trim="form.description" rows="4" :state="fieldState('description')" />
            <b-form-invalid-feedback>{{ $t('message.requiredX', [$t('label.description')]) }}</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group :label="$t('label.company')" label-cols="3" label-align="left">
            <multiselect v-model="form.company" track-by="value" label="text"
                :placeholder="$t('label.searchSelectCompany')" :options="companyDdl" :loading="companyLoading"
                :allow-empty="false" @input="companyChanged" @search-change="searchCompanies" />
            <div v-if="submitted && !$v.form.company.required" class="invalid-feedback d-block text-right">
                {{ $t('label.companyRequired') }}
            </div>
        </b-form-group>
        <b-form-group :label="$t('label.picUser')" label-cols="3" label-align="left">
            <multiselect v-model="form.picUser" track-by="value" label="text"
                :placeholder="form.company ? $t('label.searchSelectPicUser') : $t('label.selectCompanyFirst')"
                :options="userDdl" :loading="userLoading" :allow-empty="false" :disabled="!form.company"
                @search-change="searchUsers" />
            <div v-if="submitted && !$v.form.picUser.required" class="invalid-feedback d-block text-right">
                {{ $t('label.picUserRequired') }}
            </div>
        </b-form-group>
        <b-form-group :label="$t('label.pdf')" label-cols="3" label-align="left">
            <b-form-file v-model="form.pdf" accept="application/pdf" :state="pdfState"
                :placeholder="$t('label.choosePdf')" :drop-placeholder="$t('label.dropPdf')" />
            <b-form-text v-if="catalogUUID && form.pdfUrl">{{ $t('label.keepCurrentPdf') }}</b-form-text>
            <div v-if="submitted && !form.pdf && !form.pdfUrl" class="invalid-feedback d-block text-right">
                {{ $t('label.pdfRequired') }}
            </div>
        </b-form-group>
        <hr class="my-3 mx-n3">
        <div class="d-flex justify-content-end">
            <b-button type="button" variant="danger" class="mr-2" @click="$emit('done')">{{ $t('label.cancel')
                }}</b-button>
            <b-button type="submit" variant="info" :disabled="!status.save">
                <span v-if="status.save">{{ $t(catalogUUID ? 'label.updateX' : 'label.createX', [$t('module.catalog')])
                    }}</span>
                <b-spinner v-else small />
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center p-5"><b-spinner variant="info" /></div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapState } from 'vuex';
import Multiselect from 'vue-multiselect';
import { catalogMethods, lookupMethods } from '@/state/api';
import { CATALOG_MODULE } from '@/state/api/catalog.state';

export default {
    name: 'CatalogForm',
    components: { Multiselect },
    props: { catalogUUID: { type: String, default: '' }, searchCriteria: { type: Object, required: true } },
    data() {
        return {
            submitted: false,
            companyLoading: false,
            subcategoryLoading: false,
            userLoading: false,
            companyTimer: null,
            subcategoryTimer: null,
            userTimer: null,
            form: {
                uuid: '',
                name: '',
                catalogSubcategory: null,
                company: null,
                description: '',
                picUser: null,
                pdf: null, pdfUrl: ''
            },
        };
    },
    validations: {
        form: {
            name: { required },
            catalogSubcategory: { required },
            company: { required },
            description: { required },
            picUser: { required }
        }
    },
    computed: {
        ...mapState(CATALOG_MODULE, ['status']),
        details() {
            return this.$store.state.api.catalog.details || {};
        },
        companyDdl() {
            return this.$store.state.api.lookup.companyDdl || [];
        },
        catalogSubcategoryDdl() {
            return this.$store.state.api.lookup.catalogSubcategoryDdl || [];
        },
        userDdl() {
            return this.$store.state.api.lookup.userDdl || [];
        },
        pdfState() {
            return this.submitted ? Boolean(this.form.pdf || this.form.pdfUrl) : null;
        },
    },
    async created() {
        await Promise.all([this.lookupCompany(), this.lookupCatalogSubcategory()]);
        await this.loadCatalog();
    },
    beforeDestroy() { clearTimeout(this.companyTimer); clearTimeout(this.subcategoryTimer); clearTimeout(this.userTimer); },
    methods: {
        ...catalogMethods,
        ...lookupMethods,
        fieldState(field) {
            return this.submitted ? !this.$v.form[field].$invalid : null;
        },
        optionFor(options, uuid, record) {
            return options.find((option) => option.value === uuid) || (uuid ? {
                value: uuid, text: record?.name || uuid,
            } : null);
        },
        async loadCatalog() {
            if (!this.catalogUUID) return;
            const result = await this.getCatalog({ uuid: this.catalogUUID });
            if (!result) return;
            await this.lookupUser({ companyUUID: this.details.companyUUID });
            this.form = {
                ...this.form,
                ...this.details,
                pdf: null,
                catalogSubcategory: this.optionFor(this.catalogSubcategoryDdl, this.details.catalogSubcategoryUUID, this.details.catalogSubcategory),
                company: this.optionFor(this.companyDdl, this.details.companyUUID, this.details.company),
                picUser: this.optionFor(this.userDdl, this.details.picUserUUID, this.details.picUser)
            };
        },
        searchCompanies(term) {
            clearTimeout(this.companyTimer);
            this.companyTimer = setTimeout(async () => {
                this.companyLoading = true;
                await this.lookupCompany(term ? [term] : []); this.companyLoading = false;
            }, 350);
        },
        searchSubcategories(term) {
            clearTimeout(this.subcategoryTimer);
            this.subcategoryTimer = setTimeout(async () => {
                this.subcategoryLoading = true;
                await this.lookupCatalogSubcategory(term ? [term] : []); this.subcategoryLoading = false;
            }, 350);
        },
        companyChanged(company) {
            this.form.picUser = null;
            if (company) {
                this.lookupUser({ companyUUID: company.value });
            }
        },
        searchUsers(term) {
            clearTimeout(this.userTimer);
            if (!this.form.company) return;
            this.userTimer = setTimeout(async () => {
                this.userLoading = true;
                await this.lookupUser({ companyUUID: this.form.company.value, searchWords: term ? [term] : [] });
                this.userLoading = false;
            }, 350);
        },
        async submitForm() {
            this.submitted = true; this.$v.$touch();
            if (this.$v.$invalid || (!this.form.pdf && !this.form.pdfUrl)) return;
            const data = {
                ...this.form,
                catalogSubcategoryUUID: this.form.catalogSubcategory.value,
                companyUUID: this.form.company.value,
                picUserUUID: this.form.picUser.value
            };
            const action = this.catalogUUID ? this.updateCatalog : this.createCatalog;
            const result = await action({ data, filter: this.searchCriteria });
            if (result?.success) {
                this.$emit('done');
            }
        },
    },
};
</script>
